import { type ReactNode } from 'react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import {
  SideNavigation,
  SideNavigationGroup,
  SideNavigationMenuItem,
} from '@/design_system/SideNavigation/SideNavigation';
import IconAfterSales from '@/icons/AfterSales.svg';
import IconBuilding from '@/icons/Building.svg';
import IconChevron from '@/icons/Chevron.svg';
import IconCloth from '@/icons/Cloth.svg';
import IconInvoice from '@/icons/Invoice.svg';
import IconStore from '@/icons/Store.svg';
import IconTools from '@/icons/Tools.svg';
import IconPerson from '@/icons/User.svg';
import IconUsers from '@/icons/Users.svg';
import IconWorkflow from '@/icons/Workflow.svg';
import IconWorld from '@/icons/World.svg';
import { useSettingsOrganizationScope } from '@/routes/SettingsBeta/SettingsContext';
import { useCurrentOrganization, useCurrentSession } from '@/services/auth';
import { createBEMClasses } from '@/utils/classname';

import './SettingsNavbar.css';

const labels = {
  backToApp: msg({ id: 'settings-navbar.back-to-app', message: 'Back to app' }),
  personal: msg({ id: 'settings-navbar.group.personal', message: 'Personal' }),
  support: msg({ id: 'settings-navbar.group.support', message: 'Support' }),
  account: msg({ id: 'settings-navbar.account', message: 'Account' }),
  team: msg({ id: 'settings-navbar.team', message: 'Team' }),
  network: msg({ id: 'settings-navbar.network', message: 'Network' }),
  services: msg({ id: 'settings-navbar.services', message: 'Services' }),
  catalog: msg({ id: 'settings-navbar.catalog', message: 'Catalog' }),
  workflows: msg({ id: 'settings-navbar.workflows', message: 'Workflows' }),
  billing: msg({ id: 'settings-navbar.billing', message: 'Billing' }),
  organization: msg({ id: 'settings-navbar.organization', message: 'Organization' }),
  organizations: msg({ id: 'settings-navbar.organizations', message: 'Organizations' }),
  tools: msg({ id: 'settings-navbar.tools', message: 'Tools' }),
};

const { block, element } = createBEMClasses('settings-navbar');

type SettingsNavItem = {
  id: string;
  to: string;
  icon: ReactNode;
  label: string;
};

type SettingsNavGroup = {
  id: 'personal' | 'organization' | 'support';
  label: string;
  items: SettingsNavItem[];
};

export const useSettingsNavGroups = (): SettingsNavGroup[] => {
  const { _ } = useLingui();
  const { currentSession } = useCurrentSession();
  const [currentOrganization] = useCurrentOrganization();
  const organizationScope = useSettingsOrganizationScope();

  const permissions = organizationScope?.permissions ?? [];
  const canManageUsers = permissions.includes('manage_users');
  const canManageEntities = permissions.includes('manage_entities');
  const canManageOrganization = permissions.includes('manage_organization');
  const canManageBilling = permissions.includes('manage_billing');
  const isSupport = !!currentSession?.isSupport;

  const organizationItems = (
    [
      canManageUsers && {
        id: 'team',
        to: '/settings_beta/team',
        icon: <IconUsers />,
        label: _(labels.team),
      },
      canManageEntities && {
        id: 'network',
        to: '/settings_beta/network',
        icon: <IconStore />,
        label: _(labels.network),
      },
      canManageOrganization && {
        id: 'services',
        to: '/settings_beta/services',
        icon: <IconTools />,
        label: _(labels.services),
      },
      canManageOrganization && {
        id: 'catalog',
        to: '/settings_beta/catalog',
        icon: <IconCloth />,
        label: _(labels.catalog),
      },
      canManageOrganization && {
        id: 'workflows',
        to: '/settings_beta/workflows',
        icon: <IconWorkflow />,
        label: _(labels.workflows),
      },
      canManageBilling && {
        id: 'billing',
        to: '/settings_beta/billing',
        icon: <IconInvoice />,
        label: _(labels.billing),
      },
      (canManageOrganization || isSupport) && {
        id: 'organization',
        to: '/settings_beta/organization',
        icon: <IconBuilding />,
        label: _(labels.organization),
      },
    ] satisfies (SettingsNavItem | false)[]
  ).filter((item) => !!item);

  const groups: SettingsNavGroup[] = [
    {
      id: 'personal',
      label: _(labels.personal),
      items: [
        {
          id: 'account',
          to: '/settings_beta/account',
          icon: <IconPerson />,
          label: _(labels.account),
        },
      ],
    },
  ];

  if (currentOrganization && organizationItems.length) {
    groups.push({
      id: 'organization',
      label: currentOrganization.nameAlias ?? currentOrganization.name,
      items: organizationItems,
    });
  }

  if (isSupport) {
    groups.push({
      id: 'support',
      label: _(labels.support),
      items: [
        {
          id: 'organizations',
          to: '/settings_beta/support/organizations',
          icon: <IconWorld />,
          label: _(labels.organizations),
        },
        {
          id: 'tools',
          to: '/settings_beta/support/tools',
          icon: <IconAfterSales />,
          label: _(labels.tools),
        },
      ],
    });
  }

  return groups;
};

const SettingsNavbar = () => {
  const { _ } = useLingui();
  const groups = useSettingsNavGroups();

  return (
    <SideNavigation hideHeader poweredBy={false} collapsed={false} className={block()}>
      <div className={element('back')}>
        <SideNavigationMenuItem
          icon={<IconChevron left />}
          label={_(labels.backToApp)}
          to="/requests"
        />
      </div>

      {groups.map((group) => (
        <SideNavigationGroup key={group.id} label={group.label}>
          {group.items.map((item) => (
            <SideNavigationMenuItem
              key={item.id}
              icon={item.icon}
              label={item.label}
              to={item.to}
            />
          ))}
        </SideNavigationGroup>
      ))}
    </SideNavigation>
  );
};

export default SettingsNavbar;
